import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "src/store/features/auth/axiosCSRF";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";

interface iCreditHistoryItem {
    user_id: string;
    billing_month: string;
    event_type: string;
    event_type_label_ko: string;
    event_type_label_en: string;
    executed_at_utc: string;
    executed_at_kst: string;
    committed_credit: number;
    refund: number;
    status: string;
    status_label_ko: string;
    status_label_en: string;
    failure_summary: string | null;
    group_name: string;
    library_name: string;
    run_id?: string;
    group_id?: string;
    library_id?: string;
    detail?: {
        adjustment?: {
            reason?: string;
            amount?: number;
            free_after?: number;
            at?: number;
            free_before?: number;
            delta?: number;
            action?: string;
            idempotency_key?: string;
            actor_email?: string;
            at_utc?: string;
        };
        commit_summary?: {
            count?: number;
            last_event_at?: number;
            last_event_at_utc?: string;
            last_event_type?: string;
            total_committed_credit_units?: number;
            total_committed_free?: number;
            total_committed_paid?: number;
            total_committed_units?: number;
            total_refund_free?: number;
            total_refund_paid?: number;
        };
        last_event?: {
            event_type?: string;
            at?: number;
            refund_paid?: number;
            delta_committed_units?: number;
            delta_committed_paid?: number;
            refund_free?: number;
            delta_committed_credit_units?: number;
            delta_committed_free?: number;
            at_utc?: string;
            hold_status_after?: string;
        };
        start?: {
            reserved_paid?: number;
            reserve_seconds?: number;
            at?: number;
            at_utc?: string;
            attempt?: number;
            reserved_free?: number;
        };
    };
}

interface iMyCreditBalanceState {
    items: iCreditHistoryItem[];
    next_cursor: string | null;
}

export interface iUserMyBalanceHistoryState {
    response: iMyCreditBalanceState | null;
    cursorStack: (string | null)[];
    currentPage: number;
    isLoading: boolean;
    message: string | null;
}

const initialState: iUserMyBalanceHistoryState = {
    response: null,
    cursorStack: [null],
    currentPage: 0,
    isLoading: false,
    message: null,
}

export const getUserMyCreditBalanceHistoryAPI = createAsyncThunk(
    "GET_USER_MY_BALANCE_HISTORY",
    async (params: {
        month: string;
        limit?: number;
        all_user?: boolean;
        user_email?: string;
        cursor?: string | null;
        group_name?: string;
        library_name?: string;
        status?: string;
        user_id?: string;
        event_type?: string;  // 추가 권장
    }, { rejectWithValue }) => {
        try {
            const djangoEndpoint = getDjangoEndpoint();
            const pathParam = '/A7fQ9K2mXcP4R8sL/user/my-credit-history/';

            const queryParams = new URLSearchParams();
            queryParams.append('billing_month', params.month);
            if (params.limit != null)    queryParams.append('limit', String(params.limit));
            if (params.all_user != null) queryParams.append('all_user', String(params.all_user));
            if (params.user_email)       queryParams.append('user_email', params.user_email);
            if (params.cursor)           queryParams.append('cursor', params.cursor);
            if (params.group_name)       queryParams.append('group_name', params.group_name);
            if (params.library_name)     queryParams.append('library_name', params.library_name); // 추가
            if (params.status)           queryParams.append('status', params.status);             // 추가
            if (params.user_id)          queryParams.append('user_id', params.user_id);           // 추가
            if (params.event_type)       queryParams.append('event_type', params.event_type);     // 추가 권장

            const response = await axiosInstance.get(`${djangoEndpoint}${pathParam}?${queryParams.toString()}`);
            return response.data;
        } catch (e) {
            console.error('User My Balance History API 에러:', e);
            return rejectWithValue(e.response ? e.response.data : e.message);
        }
    }
)

export const userMyCreditBalanceHistorySlice = createSlice({
    name: "userMyCreditBalanceHistory",
    initialState,
    reducers: {
        goNextPage: (state) => {
            if (state.response?.next_cursor) {
                state.cursorStack.push(state.response.next_cursor);
                state.currentPage += 1;
            }
        },
        goPrevPage: (state) => {
            if (state.currentPage > 0) {
                state.cursorStack.pop();
                state.currentPage -= 1;
            }
        },
        resetPagination: (state) => {
            state.cursorStack = [null];
            state.currentPage = 0;
        },
    },
    extraReducers: (builder) => {
        builder
        .addCase(getUserMyCreditBalanceHistoryAPI.pending, (state) => {
            state.isLoading = true;
            state.message = null;
        })
        .addCase(getUserMyCreditBalanceHistoryAPI.fulfilled, (state, action) => {
            state.isLoading = false;
            state.response = action.payload;
        })
        .addCase(getUserMyCreditBalanceHistoryAPI.rejected, (state, action) => {
            state.isLoading = false;
            state.message = action.payload as string;
        })
    }
})

export const { goNextPage, goPrevPage, resetPagination } = userMyCreditBalanceHistorySlice.actions;
export default userMyCreditBalanceHistorySlice.reducer;
